import React from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import CommonButton from '@src/new-components/Button';
import { countTextLength } from '@src/services/tts';
import Tooltip from '@src/components/Tooltip';
import { REWRITE_CREDIT_PER_CHAR } from '@src/constants/project';
import useTotalCredits from '@src/hooks/useTotalCredits';
import { StyledRewrite } from './index.style';

const Rewriting = () => {
  const { t } = useTranslation();
  return (
    <div className="content-rewrite">
      <CircularProgress size={16} sx={{ color: COLOR.blueV2 }} />
      <Typography variant="body-14-regular" color={COLOR.colorGrayV2}>
        {t('contentRewriting')}
      </Typography>
    </div>
  );
};

const RewritePrompt = ({
  rewriteBlock,
  onCancelRewrite,
  onReplaceRewrite,
  onRewrite,
  textBlock,
  onOpenPopupNotEnoughCredits,
  // Two separate flags on purpose. `isSynthesizing` covers this block's own
  // audio too, and gates the two buttons that would start work on the block.
  // "Huỷ" keeps the old project-wide flag: dropping a rewrite draft while the
  // block's audio is being made is allowed, and locking it would trap the block.
  isSynthesizing = false,
  isProjectSynthesizing = false,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const costsCredit = Math.ceil(
    REWRITE_CREDIT_PER_CHAR * countTextLength(textBlock),
  );
  const costsCreditTooltip = t('costsCredit', {
    value: costsCredit?.toLocaleString(language),
  });

  const { totalCredits } = useTotalCredits();
  const handleOnRewrite = () => {
    if (totalCredits < costsCredit) {
      onOpenPopupNotEnoughCredits();
      return;
    }
    onRewrite();
  };

  return (
    <div className="rewrite-prompt">
      <AutoAwesomeRoundedIcon
        sx={{ width: 16, height: 16, color: COLOR.blueV2 }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: '100%',
        }}
      >
        <Typography
          variant="body-14-medium"
          color={COLOR.descriptionGray}
          dangerouslySetInnerHTML={{
            __html: t('contentRewritePrompt', {
              color: COLOR.textApproved,
              number: countTextLength(rewriteBlock)?.toLocaleString(language),
            }),
          }}
        />
        <Typography variant="body-14-regular" color={COLOR.descriptionGray}>
          {rewriteBlock}
        </Typography>

        <div className="rewrite-actions">
          <CommonButton
            variant="outlined"
            textProps={{
              variantText: 'body-14-regular',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '32px',
              borderRadius: '8px',
              minHeight: '32px',
              padding: '4px 12px',
            }}
            disabled={isProjectSynthesizing}
            startIcon={<CloseRoundedIcon sx={{ width: 20, height: 20 }} />}
            onClick={onCancelRewrite}
          >
            {t('cancel')}
          </CommonButton>
          <Tooltip
            title={costsCreditTooltip}
            arrow
            placement="top"
            disableHoverListener={isSynthesizing}
          >
            <div>
              <CommonButton
                variant="outlined"
                textProps={{
                  variantText: 'body-14-regular',
                  color: COLOR.darkBlue,
                }}
                customStyle={{
                  height: '32px',
                  borderRadius: '8px',
                  minHeight: '32px',
                  padding: '4px 12px',
                }}
                startIcon={
                  <RefreshRoundedIcon sx={{ width: 20, height: 20 }} />
                }
                disabled={isSynthesizing}
                onClick={handleOnRewrite}
              >
                {t('rewrite')}
              </CommonButton>
            </div>
          </Tooltip>

          <CommonButton
            variant="contained"
            textProps={{
              variantText: 'body-14-regular',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '32px',
              borderRadius: '8px',
              minHeight: '32px',
              padding: '4px 12px',
            }}
            disabled={isSynthesizing}
            startIcon={<DoneRoundedIcon sx={{ width: 20, height: 20 }} />}
            onClick={onReplaceRewrite}
          >
            {t('replace')}
          </CommonButton>
        </div>
      </div>
    </div>
  );
};

const Rewrite = ({
  rewriteBlock,
  isRewriting,
  onCancelRewrite,
  onReplaceRewrite,
  onRewrite,
  textBlock,
  onOpenPopupNotEnoughCredits,
  isRewriteFailure,
  isSynthesizing = false,
  isProjectSynthesizing = false,
}) => {
  const { t } = useTranslation();
  const renderContent = () => {
    if (isRewriteFailure && !isRewriting) {
      return (
        <div className="rewrite-failure">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <InfoRoundedIcon sx={{ color: COLOR.textRejected, fontSize: 20 }} />
            <Typography variant="body-14-regular" color={COLOR.descriptionGray}>
              {t('messageErrorRwrite')}
            </Typography>
          </div>
          <CloseRoundedIcon
            sx={{
              width: 20,
              height: 20,
              cursor: 'pointer',
              color: COLOR.navyBlue,
            }}
            onClick={onCancelRewrite}
          />
        </div>
      );
    }
    if (isRewriting) {
      return <Rewriting />;
    }
    if (rewriteBlock) {
      return (
        <RewritePrompt
          rewriteBlock={rewriteBlock}
          onCancelRewrite={onCancelRewrite}
          onReplaceRewrite={onReplaceRewrite}
          onRewrite={onRewrite}
          textBlock={textBlock}
          onOpenPopupNotEnoughCredits={onOpenPopupNotEnoughCredits}
          isSynthesizing={isSynthesizing}
          isProjectSynthesizing={isProjectSynthesizing}
        />
      );
    }
    return null;
  };

  return <StyledRewrite>{renderContent()}</StyledRewrite>;
};
export default Rewrite;
